import { Loading } from "@appkit4/react-components";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import CustomTooltip from "../../../components/customTooltip/customTooltip";
import { DataEditor } from "../../../components/ReadonlyAwareInputs";
import RoundedBorder from "../../../components/ui/RoundedBorder";
import { getMetrics } from "../../../services/calculations/EntityCalculations";
import {
  getPageTooltips,
  getSelectedPeriod,
} from "../../../services/GroupContext";
import { generateCAPId } from "../../../utils/capProcessor";
import { calculateCell, capDatesMatchPoADates } from "../util/entityInputUtils";
import EntityInputNav from "./EntityInputNav";

const EntityInputDataEditor = ({
  inputData,
  editHandler,
  capProps,
  changedMetricHighlights,
  navBarProps: {
    save,
    isPosting,
    isError,
    set_data,
    checkForAPsOverlapOrGap,
    isAPsError,
  },
}) => {
  const isClientFacing = useSelector((state) => state.usageInfo.isClientFacing);
  const tooltips = useMemo(() => getPageTooltips("entityInput"));
  const usedMetrics = useMemo(() => {
    const metrics = getMetrics(tooltips);
    if (isClientFacing) {
      const excludedMetrics = [
        "onesource_sync",
        "updated_date_db",
        "onesource_last_refresh_date_time",
        "apply_mean_accounting_date_election",
        "actual_start_date",
        "actual_end_date",
      ];

      return metrics.filter(
        (metric) => !excludedMetrics.includes(metric.metric_id),
      );
    } else {
      return metrics;
    }
  });
  const [headerColumns, setHeaderColumns] = useState([]);
  const [rowGrouping, setRowGrouping] = useState(() => ({
    groups: [
      {
        headerIndex: 0,
        isCollapsed: false,
      },
      {
        headerIndex: 10,
        isCollapsed: true,
        subGroups: [
          {
            headerIndex: 15,
            isCollapsed: false,
          },
          {
            headerIndex: 20,
            isCollapsed: false,
          },
        ],
      },
      {
        headerIndex: 30,
        isCollapsed: false,
      },
    ],
    navigationBehavior: "block",
    selectionBehavior: "block-spanning",
    themeOverride: {
      bgCell: "rgba(0, 100, 255, 0.1)",
    },
  }));
  const [loading, setLoading] = useState(true);
  const [toolTipInfo, setToolTipInfoState] = useState({});
  const selectedPeriod = getSelectedPeriod();

  const setToolTipInfo = (newData) => {
    if (toolTipInfo.message !== newData.message) setToolTipInfoState(newData);
  };

  const currentRow = useRef();

  useEffect(() => {
    const newHeaderColumns = [
      {
        id: "metric",
        title: "Metric Name",
        width: Math.max(300, window?.innerWidth / 3.5),
      },
    ];
    inputData.forEach((company) => {
      if (company) {
        newHeaderColumns.push({
          id: company.company_ID,
          title: company.company_name,
          width: 200,
        });
      }
    });
    setHeaderColumns(newHeaderColumns);

    setLoading(false);
  }, []);

  const clearToolTip = () => {
    setToolTipInfo({ message: "", positionX: 0, positionY: 0 });
  };

  const setTooltipAtPosition = (message, args) => {
    setToolTipInfo({
      message,
      positionX: args?.bounds?.x,
      positionY: args?.bounds?.y,
    });
  };

  const onItemHovered = (args) => {
    const cellType = args.kind;
    const {
      location: [column, row],
    } = args;
    const metric = usedMetrics[row];
    currentRow.current = row;

    if (cellType === "header" && column !== 0) {
      setTooltipAtPosition([headerColumns[column]?.title], args);
    } else if (cellType === "cell" && column === 0 && metric?.tooltipMessage) {
      setTimeout(() => {
        if (row === currentRow.current) {
          setTooltipAtPosition(metric?.tooltipMessage, args);
        }
      }, 1000);
    } else if (metric?.metric_id === "apply_mean_accounting_date_election") {
      const cap = inputData?.[column - 1];
      const isMADElectionDisabled = capDatesMatchPoADates(cap, selectedPeriod);

      if (isMADElectionDisabled) {
        setTooltipAtPosition(metric?.disabledToolTipMessage, args);
      } else {
        clearToolTip();
      }
    } else {
      clearToolTip();
    }
  };

  const handleColumnResize = (column, newSize) => {
    setHeaderColumns((prevColumns) => {
      return prevColumns?.map((col, index) =>
        col?.title === column?.title ? { ...col, width: newSize } : col,
      );
    });
  };

  const renderCell = ([col, row]) => {
    const metric = usedMetrics[row];
    const cap = inputData[col - 1];
    const props = capProps[col - 1];
    const capId = generateCAPId(cap);

    return calculateCell(metric, cap, col, inputData?.length, {
      ...props,
      changedMetricsForCap: changedMetricHighlights?.[capId],
    });
  };

  const onCellEdited = ([column, row], newValue) => {
    if (!inputData || !inputData[column - 1]) return;
    const metric = usedMetrics[row];
    if (!metric?.metric_id) return;
    const editedCap = inputData?.[column - 1];
    editHandler([column, row], newValue, editedCap, metric.metric_id);
  };

  return (
    <>
      <CustomTooltip data={toolTipInfo} />
      <br />
      <RoundedBorder>
        {loading ? (
          <Loading
            loadingType="linear"
            indeterminate={true}
            compact={false}
            className="page-loader"
          ></Loading>
        ) : (
          <DataEditor
            // key={renderNumber}
            width={"100vw"}
            height={"90vh"}
            smoothScrollX
            smoothScrollY
            freezeColumns={1}
            columns={headerColumns}
            onColumnResize={handleColumnResize}
            getCellContent={renderCell}
            rows={usedMetrics?.length}
            getRowId={(rows) => rows._id}
            rowGrouping={rowGrouping}
            onItemHovered={onItemHovered}
            onCellEdited={onCellEdited}
            keepNonExistentRowsSelected
            getRowThemeOverride={(i) => {
              const metric = usedMetrics[i];
              if (metric) {
                if (metric?.type === "blank") {
                  return {
                    bgCell: "#F4F3F7",
                  };
                }

                if (metric?.type === "header") {
                  return {
                    baseFontStyle: "600 18px",
                  };
                }

                if (metric?.type === "subheader") {
                  return {
                    baseFontStyle: "600 15px",
                  };
                }

                if (metric?.type === "note") {
                  return {
                    baseFontStyle: "600 12px",
                  };
                }
              }
              return {};
            }}
          />
        )}
      </RoundedBorder>

      <EntityInputNav
        data={inputData}
        isPosting={isPosting}
        set_data={set_data}
        isError={isError}
        isAPsError={isAPsError}
        save={save}
        checkForAPsOverlapOrGap={checkForAPsOverlapOrGap}
      />
    </>
  );
};

export default EntityInputDataEditor;
